import { type StalenessLevel, formatRelativeTime } from '@/utils/dateFormatters'
import { BBDTag } from '@gravitate-js/excalibrr'

/**
 * Text color + weight for the "Last Received" relative time, keyed by freshness.
 * Coloring the text (rather than filling a saturated background) stays legible
 * in both light and dark themes. All colors use CSS variables so the cell
 * adapts to the active theme (see frontend/.claude/CLAUDE.md).
 */
const LEVEL_TEXT: Record<
  StalenessLevel,
  { color: string; fontWeight: number }
> = {
  ok: { color: 'var(--foreground)', fontWeight: 400 },
  stale: { color: 'var(--theme-warning)', fontWeight: 500 },
  late: { color: 'var(--theme-error)', fontWeight: 500 },
  none: { color: 'var(--gray-6)', fontWeight: 400 },
}

interface LastReceivedRendererProps {
  receivedAt?: string | null
  level: StalenessLevel
}

/**
 * Renders the relative time since a reading was received, with the text colored
 * to reflect how stale the reading is.
 */
export function LastReceivedRenderer({
  receivedAt,
  level,
}: LastReceivedRendererProps) {
  const s = LEVEL_TEXT[level]
  return (
    <span style={{ color: s.color, fontWeight: s.fontWeight }}>
      {formatRelativeTime(receivedAt)}
    </span>
  )
}

/** Tag label + BBDTag color preset per freshness level. */
const STATUS_TAG: Record<StalenessLevel, { color: string; label: string }> = {
  ok: { color: 'success', label: 'OK' },
  stale: { color: 'warning', label: 'Stale' },
  late: { color: 'error', label: 'Late' },
  none: { color: 'default', label: 'No Readings' },
}

interface StatusRendererProps {
  level: StalenessLevel
}

/** Renders the freshness status as a colored tag. */
export function StatusRenderer({ level }: StatusRendererProps) {
  const t = STATUS_TAG[level]
  return <BBDTag color={t.color}>{t.label}</BBDTag>
}
